// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useRef, useCallback, useEffect } from 'react';
import DecisionTreeEditor from '../DecisionTreeEditor';
import DecisionTreeRenderer from '../DecisionTreeRenderer';
// import { ParameterInputPanelConnected } from '../ParameterInputPanelConnected';
import type { LegacyDecisionTree } from '../../../lib/tree';
import type { ValidationIssue, TreeEdge } from '../../../lib/validation';

export interface DecisionTreeSplitPaneProps {
  leftPaneWidth: number;
  onLeftPaneWidthChange: (width: number) => void;
  editorTree: LegacyDecisionTree;
  editorSelectedNodeId: string | null;
  onEditorTreeChange: (tree: LegacyDecisionTree) => void;
  onEditorNodeSelect: (nodeId: string | null) => void;
  rendererTree: LegacyDecisionTree;
  rendererStructureVersion: string;
  rendererSelectedNodeId: string | null;
  onRendererNodeSelect: (nodeId: string | null) => void;
  rendererShowProbabilities: boolean;
  rendererShowCosts: boolean;
  rendererShowQALYs: boolean;
  rendererShowTransitionDetails?: boolean;
  rendererNodeSize: 'S' | 'M' | 'L';
  rendererIssues: ValidationIssue[];
  rendererEdges?: TreeEdge[];
}

/**
 * Presentational split pane component for DecisionTreeBuilder
 * Handles split pane layout with drag resizing
 */
export const DecisionTreeSplitPane: React.FC<DecisionTreeSplitPaneProps> = ({
  leftPaneWidth,
  onLeftPaneWidthChange,
  editorTree,
  editorSelectedNodeId,
  onEditorTreeChange,
  onEditorNodeSelect,
  rendererTree,
  rendererStructureVersion,
  rendererSelectedNodeId,
  onRendererNodeSelect,
  rendererShowProbabilities,
  rendererShowCosts,
  rendererShowQALYs,
  rendererShowTransitionDetails = false,
  rendererNodeSize,
  rendererIssues,
  rendererEdges = [],
}) => {
  const widthRef = useRef(leftPaneWidth);
  useEffect(() => { widthRef.current = leftPaneWidth; }, [leftPaneWidth]);

  // Split pane drag handler
  const startDrag = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    const pointerId = e.pointerId;
    
    // Try to capture pointer, but don't fail if it's not supported
    try {
      if (target && typeof target.setPointerCapture === 'function') {
        target.setPointerCapture(pointerId);
      }
    } catch (err) {
      // setPointerCapture might fail in some browsers, continue anyway
      console.debug('setPointerCapture failed:', err);
    }
    
    const startX = e.clientX;
    const startW = widthRef.current;
    const MIN = 280, MAX = 560;

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const next = Math.min(MAX, Math.max(MIN, startW + (ev.clientX - startX)));
      onLeftPaneWidthChange(next);
    };
    
    const cleanup = () => {
      try {
        if (target && typeof target.releasePointerCapture === 'function') {
          target.releasePointerCapture(pointerId);
        }
      } catch (err) {
        // releasePointerCapture might fail, continue anyway
        console.debug('releasePointerCapture failed:', err);
      }
      sessionStorage.setItem('easyhta:leftPaneWidth', String(widthRef.current));
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', cleanup);
      window.removeEventListener('pointercancel', cleanup);
    };
    
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', cleanup);
    window.addEventListener('pointercancel', cleanup);
  }, [onLeftPaneWidthChange]);

  return (
    <div className="decision-tree-content">
      <div className="split-root">
        <div className="left-pane" style={{ width: leftPaneWidth }}>
          {/* Tab navigation */}
          <div className="decision-tree-tab-bar">
            {/* Placeholder for future tabs */}
            <div className="decision-tree-tab-placeholder">
              Tree Editor
            </div>
          </div>
          
          {/* Tab content */}
          <div className="left-pane__content">
            {/* {activeTab === 'editor' ? ( */}
              <div className="left-pane__scroll">
                <DecisionTreeEditor
                  tree={editorTree}
                  selectedNodeId={editorSelectedNodeId}
                  onTreeChange={onEditorTreeChange}
                  onNodeSelect={onEditorNodeSelect}
                />
              </div>
            {/* ) : (
              <div className="left-pane__scroll">
                <ParameterInputPanelConnected />
              </div>
            )} */}
          </div>
        </div>
        
        <div className="split-resizer" onPointerDown={startDrag} />
        
        <div className="right-pane">
          <DecisionTreeRenderer
            tree={rendererTree}
            structureVersion={rendererStructureVersion}
            selectedNodeId={rendererSelectedNodeId}
            onNodeSelect={onRendererNodeSelect}
            showProbabilities={rendererShowProbabilities}
            showCosts={rendererShowCosts}
            showQALYs={rendererShowQALYs}
            showTransitionDetails={rendererShowTransitionDetails}
            nodeSize={rendererNodeSize}
            issues={rendererIssues}
            edges={rendererEdges}
          />
        </div>
      </div>
    </div>
  );
};

